/**
 * WordPress dependencies
 */
import { __ } from '@wordpress/i18n';
import classnames from 'classnames';
import { Fragment, useEffect, useState} from '@wordpress/element';
import { useSelect } from '@wordpress/data';
import { useBlockProps, InnerBlocks,useInnerBlocksProps, store as blockEditorStore } from '@wordpress/block-editor';
import BlockAppender from './BlockAppender';
import ThShaper from './shaper.js';
import ResizableContainer from './ResizableContainer';
import Controls from './controls.js';
import InsSettings from './settings.js';
import './editor.css';
import { VayuBlocksDevice } from '@vayu-blocks/device-control';
import { VariationPicker } from './variationPicker';
import { VayuBlocksUniqueId, getResponsiveRangeValue, background } from '@vayu-blocks/global';
import { VayuBlockAdvanceStyle } from '@vayu-blocks/advance-panel/style.js';
import { VideoHtml, MouseFollower } from '@vayu-blocks';
export default function Edit(props) {

  const { attributes, setAttributes, clientId } = props;
  const { id, contentWidthType } = attributes;
  const deviceView = VayuBlocksDevice();

  VayuBlocksUniqueId({
    setAttributes: setAttributes,
    clientId: clientId,
  });

  const {
    parentBlock,
    hasInnerBlocks,
    parentClientId,
    defaultVariation,
    getBlockParents,
  } = useSelect(
    (select) => {
      const {
        getAdjacentBlockClientId,
        getBlock,
        getBlockRootClientId,
        getBlockParents,
        getBlocksByClientId,
      } = select('core/block-editor');
      const { getDefaultBlockVariation, getBlockVariations } = select('core/blocks');
      const block = getBlock(clientId);
      const adjacentBlockClientId = getAdjacentBlockClientId(clientId);
      const adjacentBlock = getBlock(adjacentBlockClientId);
      const parentClientId = getBlockRootClientId(clientId);
      const parentBlock = getBlock(parentClientId);
      const hasInnerBlocks = !!(block && block.innerBlocks.length);
      const blockParents = getBlockParents(clientId);
      const parentBlocks = getBlocksByClientId(blockParents);
      return {
        defaultVariation: getDefaultBlockVariation('vayu-blocks/advance-container'),
        variations: getBlockVariations('vayu-blocks/advance-container'),
        adjacentBlockClientId,
        adjacentBlock,
        parentBlock: parentBlock ? parentBlock.name : null,
        hasInnerBlocks,
        getBlockParents: blockParents,
        parentBlocks,
        parentClientId,
      };
    },
    [clientId]
  );

  const isRootBlock = !parentClientId;
  useEffect(() => {
    setAttributes({ isRootBlock });
  }, [isRootBlock]);

  const omitBy = (object, condition) =>
    Object.fromEntries(Object.entries(object).filter(([key, value]) => !condition(value)));

  let isDesktop = deviceView === 'Desktop';
  let isTablet = deviceView === 'Tablet';
  let isMobile = deviceView === 'Mobile';

  if (!id) {
    setAttributes({ id: clientId });
  }
  const { mergedStyles, isHovered, handleMouseEnter, handleMouseLeave, hoverAnimationClass } = VayuBlockAdvanceStyle(attributes,'container');
  let containerClasses = classnames(
    hoverAnimationClass,{
    [`${contentWidthType}-content`]: true,
    'th-hide-desktop': attributes.advResponsive?.Desktop === true,
    'th-hide-tablet': attributes.advResponsive?.Tablet === true,
    'th-hide-mobile': attributes.advResponsive?.Mobile === true,
    'th-has-child': hasInnerBlocks ? true : false,
  });

  const verticalAlignValues = {
    top: 'flex-start',
    center: 'center',
    bottom: 'flex-end',
  };

  let containerStyles = {};
  let insidecontainerStyles = {};
  let flexcontainerStyles = {};
  let ShaperStyle = {};
  let gapi = {};
  const globalcontainerWidth = vayublock.options.global.containerWidth;
  const globalcontainerGap = vayublock.options.global.containerGap + 'px';

  let ResizeWidth;

  if (isDesktop) {
    ResizeWidth = getResponsiveRangeValue(attributes.fullcontentWidth, 'Desktop') || '100%';
    containerStyles = {
	  ...(contentWidthType === 'fullwidth' ? { width: ResizeWidth } : {width:'100%'}),
    };

    if (contentWidthType === 'boxed' && Object.values(attributes.boxedcontentWidth).some(val => !!val)) {
      insidecontainerStyles = {
        maxWidth: getResponsiveRangeValue(attributes.boxedcontentWidth, 'Desktop'),
        marginLeft: 'auto',
        marginRight: 'auto',
		    width: '100%',
      };
    } else if(attributes.contentWidthType !=='fullwidth'){
      insidecontainerStyles = {
        maxWidth: globalcontainerWidth + 'px',
        marginLeft:'auto',
        marginRight:'auto',
        width: '100%',
      }; 
	}

    if (attributes.contentMinHgt) {
      insidecontainerStyles = {
        ...insidecontainerStyles,
        minHeight: getResponsiveRangeValue(attributes.contentMinHgt, 'Desktop'),
      };
    }

     flexcontainerStyles = {
      flexDirection: attributes.contFlex?.direction?.Desktop,
      justifyContent: attributes.contFlex?.justifyContent?.Desktop,
      alignItems: attributes.contFlex?.alignItems?.Desktop,
      flexWrap: attributes.contFlex?.wrap?.Desktop,
    };

    const hasValidFlexPropertiesD = Object.values(flexcontainerStyles).some(value => value !== undefined);
    // Add display: 'flex' only if at least one property is defined
    if (hasValidFlexPropertiesD) {
      flexcontainerStyles = {
        ...flexcontainerStyles,
        display: 'flex',
      };
    }
    // Handle alignContent if flexWrap is 'wrap'
    if (attributes.contFlex?.wrap?.Desktop === 'wrap') {
      flexcontainerStyles = {
        ...flexcontainerStyles,
        alignContent: attributes.contFlex?.alignContent?.Desktop,
      };
    }

    ShaperStyle = {
      '--shaper-top-width': getResponsiveRangeValue(attributes.shapeTopWidth, 'Desktop'),
      '--shaper-top-height': getResponsiveRangeValue(attributes.shapeTopHeight, 'Desktop'),
      '--shaper-bottom-width': getResponsiveRangeValue(attributes.shapeBottomWidth, 'Desktop'),
      '--shaper-bottom-height': getResponsiveRangeValue(attributes.shapeBottomHeight, 'Desktop'),
      '--shaper-z-index-top': attributes.shapeTopFront ? 1 : 0,
      '--shaper-z-index-bottom': attributes.shapeBottomFront ? 1 : 0,
    };

    gapi = {
      rowGap:
        attributes.contFlex?.rwGap?.Desktop !== null &&
        attributes.contFlex?.rwGap?.Desktop !== undefined
          ? `${attributes.contFlex?.rwGap?.Desktop}`
          : `${globalcontainerGap}`,
      columnGap:
        attributes.contFlex?.colGap?.Desktop !== null &&
        attributes.contFlex?.colGap?.Desktop !== undefined
          ? `${attributes.contFlex?.colGap?.Desktop}`
          : `${globalcontainerGap}`,
    };
  }

  if (isTablet) {
    ResizeWidth = getResponsiveRangeValue(attributes.fullcontentWidth, 'Tablet');
    containerStyles = {
      width: ResizeWidth,
    };

    if (contentWidthType === 'boxed') {
      insidecontainerStyles = {
        maxWidth: getResponsiveRangeValue(attributes.boxedcontentWidth, 'Tablet'),
		    width: '100%',
        marginLeft: 'auto',
        marginRight: 'auto',
      };
    } else if (contentWidthType !== 'fullwidth') {
      insidecontainerStyles = {
        maxWidth: globalcontainerWidth + 'px',
		    width: '100%',
        marginLeft: 'auto',
        marginRight: 'auto',
      };
    } 

    if (attributes.contentMinHgt) {
      insidecontainerStyles = {
        ...insidecontainerStyles,
        minHeight: getResponsiveRangeValue(attributes.contentMinHgt, 'Tablet'),
      };
    }

    flexcontainerStyles = {
      flexDirection: attributes.contFlex?.direction?.Tablet,
      justifyContent: attributes.contFlex?.justifyContent?.Tablet,
      alignItems: attributes.contFlex?.alignItems?.Tablet,
      flexWrap: attributes.contFlex?.wrap?.Tablet,
    };

    const hasValidFlexPropertiesM = Object.values(flexcontainerStyles).some(value => value !== undefined);
    // Add display: 'flex' only if at least one property is defined
    if (hasValidFlexPropertiesM) {
      flexcontainerStyles = {
        ...flexcontainerStyles,
        display: 'flex',
      };
    }
    if (attributes.contFlex?.wrap?.Tablet === 'wrap') {
      flexcontainerStyles = {
        ...flexcontainerStyles,
        alignContent: attributes.contFlex?.alignContent?.Tablet,
      };
    }

    ShaperStyle = {
      '--shaper-top-width': getResponsiveRangeValue(attributes.shapeTopWidth, 'Tablet'),
      '--shaper-top-height': getResponsiveRangeValue(attributes.shapeTopHeight, 'Tablet'),
      '--shaper-bottom-width': getResponsiveRangeValue(attributes.shapeBottomWidth, 'Tablet'),
      '--shaper-bottom-height': getResponsiveRangeValue(attributes.shapeBottomHeight, 'Tablet'),
      '--shaper-z-index-top': attributes.shapeTopFront ? 1 : 0,
      '--shaper-z-index-bottom': attributes.shapeBottomFront ? 1 : 0,
    };

    gapi = {
      rowGap:
        attributes.contFlex?.rwGap?.Tablet !== null &&
        attributes.contFlex?.rwGap?.Tablet !== undefined
          ? `${attributes.contFlex?.rwGap?.Tablet}`
          : `${globalcontainerGap}`,
        columnGap:
        attributes.contFlex?.colGap?.Tablet !== null &&
        attributes.contFlex?.colGap?.Tablet !== undefined
          ? `${attributes.contFlex?.colGap?.Tablet}`
          : `${globalcontainerGap}`,
    };
  }

  if (isMobile) {
    ResizeWidth = getResponsiveRangeValue(attributes.fullcontentWidth, 'Mobile');
    containerStyles = {
      width: ResizeWidth,
    };

    if (contentWidthType === 'boxed') {
      insidecontainerStyles = {
        maxWidth: getResponsiveRangeValue(attributes.boxedcontentWidth, 'Mobile'),
        marginLeft: 'auto',
        marginRight: 'auto',
      };
    } else if (contentWidthType !== 'fullwidth') {
      insidecontainerStyles = {
        maxWidth: globalcontainerWidth + 'px',
        marginLeft: 'auto',
        marginRight: 'auto',
      };
    } 

    if (attributes.contentMinHgt) {
      insidecontainerStyles = {
        ...insidecontainerStyles,
        minHeight: getResponsiveRangeValue(attributes.contentMinHgt, 'Mobile'),
      };
    }

    flexcontainerStyles = {
      flexDirection: attributes?.contFlex?.direction?.Mobile ?? 'column',
      justifyContent: attributes.contFlex?.justifyContent?.Mobile,
      alignItems: attributes.contFlex?.alignItems?.Mobile,
      flexWrap: attributes.contFlex?.wrap?.Mobile,
    };

    

    const hasValidFlexProperties = Object.values(flexcontainerStyles).some(value => value !== undefined);
    // Add display: 'flex' only if at least one property is defined
    if (hasValidFlexProperties) {
      flexcontainerStyles = {
        ...flexcontainerStyles,
        display: 'flex',
      };
    }

    // Handle alignContent if flexWrap is 'wrap'
    if (attributes.contFlex?.wrap?.Mobile === 'wrap') {
      flexcontainerStyles = {
        ...flexcontainerStyles,
        alignContent: attributes.contFlex?.alignContent?.Mobile,
      };
    }
    
    ShaperStyle = {
      '--shaper-top-width': getResponsiveRangeValue(attributes.shapeTopWidth, 'Mobile'),
      '--shaper-top-height': getResponsiveRangeValue(attributes.shapeTopHeight, 'Mobile'),
      '--shaper-bottom-width': getResponsiveRangeValue(attributes.shapeBottomWidth, 'Mobile'),
      '--shaper-bottom-height': getResponsiveRangeValue(attributes.shapeBottomHeight, 'Mobile'),
      '--shaper-z-index-top': attributes.shapeTopFront ? 1 : 0,
      '--shaper-z-index-bottom': attributes.shapeBottomFront ? 1 : 0,
    };

    gapi = {
      rowGap:
        attributes.contFlex?.rwGap?.Mobile !== null &&
        attributes.contFlex?.rwGap?.Mobile !== undefined
          ? `${attributes.contFlex?.rwGap?.Mobile}`
          : `${globalcontainerGap}`,
       columnGap:
        attributes.contFlex?.colGap?.Mobile !== null &&
        attributes.contFlex?.colGap?.Mobile !== undefined
          ? `${attributes.contFlex?.colGap?.Mobile}`
          : `${globalcontainerGap}`,
    };
  }
  

  const isChildContainer = parentBlock;
  const isParentContainer = !isChildContainer;
  const Tag = attributes.containerHTMLTag;

  if (attributes.verticalAlign) {
    insidecontainerStyles = {
      ...insidecontainerStyles,
      alignItems: verticalAlignValues[attributes.verticalAlign],
    };
  }

  //Overlay settings
  let cntBackground;
  let cntBackgroundHover;
  
  cntBackground = {
      ...background(attributes.advBackground),
  };
  
  cntBackgroundHover = {
      ...background(attributes.advBackgroundHover),
  };
  
  const filteredcntBgHoverStyles = omitBy( cntBackgroundHover, value => value === undefined || value === null);

  // Debug logs to diagnose the issue

  const style = omitBy(
    {
      ...mergedStyles,
      ...containerStyles,
      ...ShaperStyle,
      ...cntBackground,
      ...(isHovered ? filteredcntBgHoverStyles : {}),
    },
    (x) => x?.includes?.('undefined') || x === undefined
  );

  const blockProps = useBlockProps({
    id: `th-block-container-${attributes.uniqueId}`,
    className: containerClasses,
    style,
  });

  const innerstyle = omitBy(
    {
      ...insidecontainerStyles,
      ...flexcontainerStyles,
      ...gapi,
    },
    (x) => x?.includes?.('undefined') || x === undefined
  );

  const isSelected = useSelect(
    (select) => {
      return select('core/block-editor').getBlockSelectionStart() === clientId;
    },
    [clientId]
  );

const isFullWidth = contentWidthType === 'fullwidth';

const className = isFullWidth
  ? 'vb-block-editor'
  : 'th-inside-container th-con';

  const innerBlocksProps = useInnerBlocksProps(
    {
      style: innerstyle,
      className
    },
    {
      templateLock: false,
      renderAppender: InnerBlocks.ButtonBlockAppender,
    }
  );

  const containerBlockProps = {
    ...blockProps,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
  };

  const supportsLayout = useSelect((select) => {
    const { getSettings } = select(blockEditorStore);
    return getSettings().supportsLayout || false;
  }, []);

  let contentWidthClass = '';
  if (contentWidthType === 'alignwide') {
    contentWidthClass = 'alignwide';
  } else if (contentWidthType === 'boxed' || contentWidthType === 'fullwidth') {
    contentWidthClass = 'alignfull';
  }

  let rootContainerClassName = supportsLayout
    ? `th-root-block-container ${contentWidthClass} th-block-container-${attributes.uniqueId} wp-block`
    : 'wp-block';

  let innerBlockss = [];
  try {
    const block = wp.data.select('core/block-editor').getBlock(clientId);
    if (block && block.innerBlocks) {
      innerBlockss = block.innerBlocks;
    }
  } catch (error) {
    // console.error('Error while accessing innerBlocks:', error);
  }

    const mergedBlockProps = {
      ...containerBlockProps,
      className: classnames(
        containerBlockProps.className,
        contentWidthType === 'fullwidth' ? innerBlocksProps.className : '',
        {[`${attributes.uniqueId}`]: true,
          'vayu-container-parent': isParentContainer,
          'vayu-container-child': isChildContainer,
          alignfull: !isChildContainer && (contentWidthType === 'fullwidth' || contentWidthType === 'boxed'),
          alignwide: !isChildContainer && contentWidthType === 'alignwide',
          'vb_marqee_slide': isParentContainer && attributes.innerContSlide === 'marquee-slide',
        }),
      style: {
        ...containerBlockProps.style,
      },
    };

     //Overlay settings
      let overlayBackground;
      let overlayBackgroundHover;
      
      overlayBackground = {
          ...background(attributes.overlaybackground),
      };
      
      overlayBackgroundHover = {
          ...background(attributes.overlaybackgroundHover),
      };
      
      const filteredHoverStyles = omitBy( overlayBackgroundHover, value => value === undefined || value === null);
  
     // Define overlay style, switching between normal and hover states
      const overlayStyle = {
          ...overlayBackground,
          ...(isHovered ? filteredHoverStyles : {}),
          transition: 'background 0.3s ease',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          maxWidth:'100%',
      };

  return (
    <Fragment>
       {isSelected && <Controls {...props} />}
       {isSelected && <InsSettings props={props} globalcontainerWidth={globalcontainerWidth} />}
      {!attributes.variationSelected && innerBlockss.length === 0 && getBlockParents.length === 0 ? (
      <VariationPicker {...{ clientId, setAttributes, defaultVariation }} />
      ) : (
      <Fragment>
		  {(parentBlock === 'vayu-blocks/advance-container' || !hasInnerBlocks) ? (
			<Tag {...mergedBlockProps} >
      <VideoHtml bg={attributes.advBackground} />
      <div className="wp-block-th-blocks-container-overlay" style={overlayStyle}/>
      <ThShaper attributes={attributes} />
			<div {...innerBlocksProps}>{innerBlocksProps.children}</div>
       <MouseFollower value={attributes.advFollower} clientId={clientId}/>
			</Tag>
		  ) : (
			(!isChildContainer) ? (
			<div className={rootContainerClassName}>
				<Tag {...mergedBlockProps} >
        <VideoHtml bg={attributes.advBackground} />
        <div  className="wp-block-th-blocks-container-overlay" style={overlayStyle}/>
        <ThShaper attributes={attributes} />
				<div {...innerBlocksProps}>{innerBlocksProps.children}</div>
         <MouseFollower value={attributes.advFollower} clientId={clientId}/>
				</Tag>
			</div>
			) : (
      <>
			<Tag {...mergedBlockProps} >
      <VideoHtml bg={attributes.advBackground} />
      <div  className="wp-block-th-blocks-container-overlay" style={overlayStyle}/>
      <ThShaper attributes={attributes} />
      <div {...innerBlocksProps}>{innerBlocksProps.children}</div>
       <MouseFollower value={attributes.advFollower} clientId={clientId}/>
			</Tag>
      </>
			)
		)}
		</Fragment>
      )}
    </Fragment>
  );
}